/**
 * OfflineIndicator — Sprint v28.
 *
 * Small floating pill in the bottom-right that shows offline queue
 * status. Only renders when the queue has pending items or the user
 * is offline. Silent otherwise.
 */
import React, { useEffect, useState } from 'react';
import { offlineQueue, QueueStatus } from '../lib/offlineQueue';
import { WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { useT } from '../contexts/LocaleContext';

export const OfflineIndicator: React.FC = () => {
  const t = useT();
  const [status, setStatus] = useState<QueueStatus>('idle');
  const [pending, setPending] = useState<number>(0);
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );

  useEffect(() => {
    const unsubscribe = offlineQueue.subscribe((s, p) => {
      setStatus(s);
      setPending(p);
    });
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      unsubscribe();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Silent when everything is fine.
  if (online && pending === 0 && status === 'idle') return null;

  const label = !online
    ? t('common.offline')
    : status === 'syncing'
      ? `${t('common.syncing')} (${pending})`
      : status === 'error'
        ? `${t('common.retrying')} (${pending})`
        : pending > 0
          ? `${pending} ${pending > 1 ? t('common.pendingPlural') : t('common.pending')}`
          : t('common.ready');

  const Icon = !online
    ? WifiOff
    : status === 'syncing'
      ? RefreshCw
      : status === 'error'
        ? AlertCircle
        : RefreshCw;

  const bg = !online
    ? 'rgba(220, 38, 38, 0.15)'
    : status === 'error'
      ? 'rgba(234, 88, 12, 0.15)'
      : 'rgba(6, 182, 212, 0.15)';
  const border = !online
    ? 'rgba(220, 38, 38, 0.4)'
    : status === 'error'
      ? 'rgba(234, 88, 12, 0.4)'
      : 'rgba(6, 182, 212, 0.4)';
  const color = !online
    ? '#ef4444'
    : status === 'error'
      ? '#f97316'
      : '#22d3ee';

  return (
    <div
      data-testid="offline-queue-indicator"
      onClick={() => { void offlineQueue.flush(); }}
      style={{
        position: 'fixed',
        bottom: 'calc(env(safe-area-inset-bottom, 0) + 16px)',
        right: 16,
        zIndex: 60,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 14px',
        borderRadius: 999,
        background: bg,
        border: `1px solid ${border}`,
        color,
        backdropFilter: 'blur(10px)',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        letterSpacing: '0.02em',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
      }}
      title={t('common.syncRetry')}
    >
      <Icon
        size={14}
        className={status === 'syncing' ? 'animate-spin' : ''}
      />
      {label}
    </div>
  );
};
